import React, { useEffect, useState } from 'react';
import { Button, Card, CardFooter, Col, Row } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { startOfMonth } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import CallexLoader from '../../../components/CallexLoader';
import AdminHeader from '../../../components/header/AdminHeader';
import FormDateInput from '../../../components/form/FormDateInput';
import { checkModulePermission, formatPayloadDate } from '../../../utils/common';

import { useUserRolePermission } from '../../../store/slices/authSlice';
import { useTimezone } from '../../../store/slices/commonSlice';
import { botCreditReportGet } from '../../../store/slices/tenantSlices/dailyReportSlice';

const PERMISSION_KEY = 'Credit Report';

// Numeric/amount columns are right-aligned.
const RIGHT_ALIGN_FIELDS = ['balance_before', 'credit', 'balance_after', 'amount'];

const renderCell = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const CreditReport = () => {
  const dispatch = useDispatch();
  const userRolePermission = useUserRolePermission();
  const timezone = useTimezone();
  const today = toZonedTime(new Date(), timezone);

  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);

  const initialValues = {
    startDate: startOfMonth(today),
    endDate: today
  };

  const validationSchema = Yup.object({
    startDate: Yup.date().required('Start date is required'),
    endDate: Yup.date().required('End date is required')
  });

  const onSubmit = async (valuesToUse) => {
    setIsLoading(true);
    setIsFilterOpen(false);
    try {
      const payload = {
        start_date: formatPayloadDate(valuesToUse.startDate),
        end_date: formatPayloadDate(valuesToUse.endDate)
      };
      const response = await dispatch(botCreditReportGet(payload)).unwrap();
      setRows(Array.isArray(response?.data) ? response.data : []);
      setHeaders(Array.isArray(response?.header) ? response.header : []);
    } catch (error) {
      console.log('error', error);
      setRows([]);
      setHeaders([]);
    } finally {
      setIsLoading(false);
    }
  };

  const { values, handleBlur, setFieldValue, handleSubmit, touched, errors } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (v) => onSubmit(v)
  });

  useEffect(() => {
    onSubmit(values);
  }, []);

  return (
    <div>
      <AdminHeader
        title="Credit Report"
        isSearch
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
      />

      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  name="startDate"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  label="From Date"
                  showTimeSelect={true}
                  value={values.startDate}
                  maxDate={values.endDate || today}
                  onChange={(e) => setFieldValue('startDate', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  name="endDate"
                  label="To Date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  showTimeSelect={true}
                  minDate={values.startDate}
                  maxDate={today}
                  value={values.endDate}
                  onChange={(e) => setFieldValue('endDate', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit">
                Search
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}

      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 328px)' : 'calc(100vh - 165px)' }}>
          <CallexLoader />
        </Card>
      ) : rows.length > 0 &&
        checkModulePermission('is_view', PERMISSION_KEY, userRolePermission) ? (
        <Card className="p-0 position-relative m-0">
          <div
            className="daily-report-table"
            style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  {headers.map((col) => (
                    <th
                      key={col.name}
                      className={RIGHT_ALIGN_FIELDS.includes(col.name) ? 'text-end' : ''}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  // debit = deduction (red, -), credit = addition (green, +)
                  const isDebit = String(row?.transaction_type || '').toLowerCase() === 'debit';
                  const amountClass = isDebit ? 'text-danger fw-bold' : 'text-success fw-bold';
                  return (
                    <tr key={row?.id || index}>
                      {headers.map((col) => {
                        const alignClass = RIGHT_ALIGN_FIELDS.includes(col.name) ? 'text-end' : '';
                        if (col.name === 'credit') {
                          const val = row?.[col.name];
                          const hasVal = val !== null && val !== undefined && val !== '';
                          return (
                            <td key={col.name} className={`${amountClass} ${alignClass}`.trim()}>
                              {hasVal ? `${isDebit ? '-' : '+'}${val}` : '-'}
                            </td>
                          );
                        }
                        return (
                          <td key={col.name} className={alignClass}>
                            {renderCell(row?.[col.name])}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 328px)' : 'calc(100vh - 165px)' }}>
          No data available
        </Card>
      )}
    </div>
  );
};

export default CreditReport;
